import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector } from '../../store';

// UTILS
import { SITE_KEY, validationErrorsToString } from '../../utils';
import { queryStringToObject } from '../../utils';

// PACKAGES
import { useHistory, useLocation } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import RegisterOrganisationForm from './RegisterOrganisationForm';
import RegisterOrganisationSummary from './RegisterOrganisationSummary';
import RegisterOrganisationPayment from './RegisterOrganisationPayment';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { OrganisationRegistrationCommand } from '../../api/models';

interface RegisterOrganisationTypes {}

export const RegisterOrganisation: React.FC<RegisterOrganisationTypes> = ({}) => {
	const location = useLocation();
	const history = useHistory();

	// Error and Loading state
	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [currentOrganisationId, setCurrentOrganisationId] = React.useState<string>('');

	const [organisationDetails, setOrganisationDetails] = React.useState<OrganisationRegistrationCommand>({
		paymentCycle: 'Monthly',
		// Get paymentPlan id from react-router state for initial load
		paymentPlanId: location.state ? location.state.paymentPlanId : ''
	});

	const [planName, setPlanName] = React.useState<string>('');

	const getOrganisationDetails = async (organisationId: string) => {
		setIsLoading(true);

		const response = await apibridge.getOrganisationRegistration(organisationId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setIsLoading(false);

			setOrganisationDetails(response.result);
		}
	};

	React.useEffect(() => {
		const queryParams = queryStringToObject();
		const organisationId = queryParams.id;

		if (organisationId) {
			setCurrentOrganisationId(queryParams.id);

			getOrganisationDetails(organisationId);
		}
		setIsLoading(false);
	}, []);

	const register = async (values: OrganisationRegistrationCommand, token: string) => {
		setIsLoading(true);

		values.captchaToken = token;

		const response = await apibridge.doRegisterOrganisation(values);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setIsLoading(false);

			if (currentOrganisationId) {
				history.push(`/register-organisation-payment?id=${currentOrganisationId}`);
			} else {
				history.push(`/register-organisation-payment?id=${response.result.organisationId}`);
			}
		}
	};

	const doRegister = async (values: OrganisationRegistrationCommand) => {
		setIsLoading(true);

		await window.grecaptcha.ready(() => {
			window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token: string) => {
				register(values, token);
			});
		});
	};

	return (
		<div className="page-register-organisation">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<RegisterOrganisationForm
									organisationDetails={organisationDetails}
									setOrganisationDetails={setOrganisationDetails}
									planName={planName}
									doRegister={doRegister}
								/>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default RegisterOrganisation;
